/**
 * AP-28 — **die Bausteine der Artikelmasken**: Abschnittskarte, Feld und die
 * Feldklassen. Geteilt von Detail (`ArticleDetailPage`) und Anlage
 * (`ArticleNewPage`), damit beide gleich aussehen.
 *
 * Zahlenfelder haben eine **feste, gebräuchliche Breite** statt der vollen
 * Spalte (Wunsch vom 13.9.2026): Ein Preis über 30 Zeichen Breite, rechtsbündig,
 * steht weit weg von seiner Beschriftung. Rechtsbündig bleiben sie, damit
 * Kommastellen untereinander stehen.
 *
 * Seit AP-36 Phase E gibt es jede Feldklasse zweimal: `eingabe`/`zahlEingabe`
 * für die Anlageseite mit ihrem Knopf, und `…Klasse` auf Grundlage von
 * `feldKlasse` für das Detail, das feldweise ohne Knopf speichert — dort sieht
 * ein gesperrtes Feld aus wie Text (AP-36 4.1).
 */

import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { PRICE_UNIT_MAX, PRICE_UNIT_SUGGESTIONS } from '@symphenia/shared-types'
import { feldKlasse } from '@/lib/speichern'

const feldBasis =
  'rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-70'

/** Text, Auswahl, Textbereich — volle Breite. */
export const eingabe = `w-full ${feldBasis}`

/** Preis, Preiseinheit, Steuersatz, Lieferzeit, VPE — 8rem. */
export const zahlEingabe = `w-32 ${feldBasis} text-right tabular-nums`

/** Gewicht mit Einheit im Feld („kg / Stk") — breiter, damit die Zahl Platz neben dem Zusatz hat. */
export const gewichtEingabe = `w-44 ${feldBasis} pr-16 text-right tabular-nums`

// ── Dieselben Breiten für die Felder des Speicher-Bausteins (AP-36 Phase E) ──
//
// `feldKlasse` bringt `w-full` mit; eine feste Breite muss sie deshalb mit `!`
// überstimmen — dasselbe Muster wie in `ArticleSettingsPage`.

/** Preis, Preiseinheit, Steuersatz, Lieferzeit, VPE — 8rem. */
export const zahlKlasse = `${feldKlasse} !w-32 text-right tabular-nums`

/** Gewicht mit Einheit im Feld — Platz für den Zusatz rechts. */
export const gewichtKlasse = `${feldKlasse} !w-44 pr-16 text-right tabular-nums`

export function Abschnittskarte({
  titel,
  aktion,
  children,
}: {
  titel: string
  aktion?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2">
        <h2 className="font-semibold text-foreground">{titel}</h2>
        {aktion}
      </div>
      <div className="px-4 py-4">{children}</div>
    </section>
  )
}

export function Feld({ label, hinweis, children }: { label: string; hinweis?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-foreground">{label}</label>
      {children}
      {hinweis && <p className="mt-1 text-xs text-muted-foreground">{hinweis}</p>}
    </div>
  )
}

export function SpeichernKnopf({ disabled, label }: { disabled?: boolean; label: string }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
    >
      {label}
    </button>
  )
}

/**
 * Rahmen, der „kg / Stk" rechts ins Gewichtsfeld setzt. Das Feld selbst kommt
 * von außen: die Anlageseite reicht ein eigenes, das Detail eines des
 * Speicher-Bausteins.
 */
export function GewichtRahmen({ einheit, children }: { einheit: string; children: React.ReactNode }) {
  const { t } = useTranslation()
  return (
    <div className="relative w-44">
      {children}
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">
        {t('articles.weightSuffix', { unit: einheit })}
      </span>
    </div>
  )
}

/** Gewicht mit „kg / Stk" im Feld. */
export function GewichtFeld({
  value,
  onChange,
  einheit,
  disabled,
}: {
  value: string
  onChange: (text: string) => void
  einheit: string
  disabled?: boolean
}) {
  return (
    <GewichtRahmen einheit={einheit}>
      <input inputMode="decimal" disabled={disabled} value={value} onChange={(e) => onChange(e.target.value)} className={gewichtEingabe} />
    </GewichtRahmen>
  )
}

/**
 * Die Preiseinheit als Zahlenfeld mit Vorschlägen — die Fassung, die die Props
 * des Speicher-Bausteins entgegennimmt (`{...feld.props}`). Regeln und Grenze
 * sind dieselben wie in `PriceUnitInput`, das die Anlageseite mit ihrem eigenen
 * Zustand benutzt.
 */
export function PreiseinheitEingabe({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  const listId = useId()
  return (
    <>
      <input type="number" inputMode="numeric" min={1} max={PRICE_UNIT_MAX} step={1} list={listId} className={className} {...props} />
      <datalist id={listId}>
        {PRICE_UNIT_SUGGESTIONS.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>
    </>
  )
}
